import React, { FC, ReactNode, useEffect } from 'react';

import { DateTime, Interval } from 'luxon';
import { FormattedMessage } from 'react-intl';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ButtonType, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';
import { useGamePolling } from '@nhl/react-hooks/lib/useGamePolling';

import CommonMessages from '~/core/commonMessages';
import NoContent from '~/views/components/noContent/NoContent';

import { useClubScheduleMonthQuery } from '../../api';
import { ScheduleViewType } from '../../enums';
import { ClubScheduleButton } from '../../styles';
import { ViewProps } from '../../types';

import Calendar from './components/calendar/Calendar';

import {
    BottomControls,
    ColorBox,
    ColorCodeContainer,
    ColorGroup,
    ColorType,
    MonthYear,
    PromotionsContainer,
    TopBar,
} from './styles';

const formatPromotionsLabel = (date: DateTime): ReactNode => {
    //TODO: Change this to the correct data when the api is updated
    const dateSpan = Interval.fromDateTimes(date.month <= 6 ? date.minus({ years: 1 }) : date, date.plus({ years: 1 }));

    return (
        <FormattedMessage
            description="A label for the promotions available for a given season"
            defaultMessage="View {start}-{end} Theme Nights and Promotions"
            values={{
                start: dateSpan.start?.toLocaleString({ year: 'numeric' }),
                end: dateSpan.end?.toLocaleString({ year: '2-digit' }),
            }}
        />
    );
};

const CalendarView: FC<ViewProps> = ({
    dateFromURL,
    handleNextOrPreviousDate,
    setSchedule,
    setScheduleDate,
    setClubCalendarLink,
    timezone,
    isMedium,
}) => {
    const teamAbbrev = ShortCodeEnums.TeamTriCode[window.__SITE_SETTINGS__.franchise.triCode.toUpperCase()];
    let monthToRequest: string;

    if (dateFromURL === 'now') {
        monthToRequest = dateFromURL;
    } else {
        const parsedDate = DateTime.fromISO(dateFromURL);
        monthToRequest = `${parsedDate.toLocaleString({ year: 'numeric' })}-${parsedDate.toLocaleString({
            month: '2-digit',
        })}`;
    }

    const {
        data: schedule,
        isLoading,
        isInitialLoad,
        resolvedDate,
        refetch,
    } = useClubScheduleMonthQuery(teamAbbrev, monthToRequest);

    useGamePolling(refetch, schedule.games || []);

    useEffect(() => {
        const dateOfFirstGame = DateTime.fromISO(schedule.games?.[0]?.gameDate);

        setSchedule(schedule);
        setScheduleDate(dateOfFirstGame.isValid ? dateOfFirstGame : resolvedDate);
    }, [schedule, setSchedule, setScheduleDate, resolvedDate]);

    useEffect(() => {
        if (schedule.calendarUrl) {
            setClubCalendarLink(schedule.calendarUrl);
        }
    }, [schedule, setClubCalendarLink]);

    const getSubsequentMonth = (date: DateTime, next: boolean): DateTime =>
        next ? date.plus({ months: 1 }) : date.minus({ months: 1 });

    const hasPromotions = !!schedule.promotionLink;

    const noGamesText = (
        <FormattedMessage
            description="There are no games scheduled for this month."
            defaultMessage="There are no games scheduled for this month."
        />
    );

    return (
        <div id={`${ScheduleViewType.CALENDAR}-tabpanel`} role="tabpanel" aria-labelledby={ScheduleViewType.CALENDAR}>
            {isInitialLoad || !resolvedDate ? (
                <LoadingDisplay isLoading={true} />
            ) : (
                <LoadingDisplay isLoading={isLoading}>
                    <TopBar>
                        <MonthYear>{resolvedDate.toLocaleString({ month: 'long', year: 'numeric' })}</MonthYear>
                        {schedule.promotionLink && (
                            <PromotionsContainer
                                href={schedule.promotionLink}
                                hidden={schedule.games.length === 0 || !hasPromotions}
                            >
                                {formatPromotionsLabel(resolvedDate)}
                            </PromotionsContainer>
                        )}
                        <ColorCodeContainer hidden={schedule.games.length === 0}>
                            <ColorGroup>
                                <ColorBox $isHome $teamAbbrev={teamAbbrev}></ColorBox>
                                <ColorType>
                                    <FormattedMessage {...CommonMessages.gameLocationDescriptions.short.home} />
                                </ColorType>
                            </ColorGroup>
                            <ColorGroup>
                                <ColorBox></ColorBox>
                                <ColorType>
                                    <FormattedMessage {...CommonMessages.gameLocationDescriptions.short.away} />
                                </ColorType>
                            </ColorGroup>
                        </ColorCodeContainer>
                    </TopBar>
                    {schedule.games.length === 0 ? (
                        <NoContent primaryText={noGamesText} />
                    ) : (
                        <Calendar
                            key={schedule.currentMonth}
                            date={resolvedDate}
                            games={schedule.games}
                            team={teamAbbrev}
                            timezone={timezone}
                        />
                    )}
                    <BottomControls hidden={schedule.games.length === 0}>
                        <ClubScheduleButton
                            variant={ButtonType.Secondary}
                            onClick={(e) =>
                                handleNextOrPreviousDate(
                                    getSubsequentMonth(resolvedDate, false),
                                    e.target as HTMLButtonElement
                                )
                            }
                        >
                            <ChevronLeftIcon />
                            {isMedium ? (
                                getSubsequentMonth(resolvedDate, false).toLocaleString({
                                    month: 'short',
                                    year: 'numeric',
                                })
                            ) : (
                                <FormattedMessage
                                    description="A word describing the previous month"
                                    defaultMessage="Previous"
                                />
                            )}
                        </ClubScheduleButton>
                        <ClubScheduleButton
                            variant={ButtonType.Secondary}
                            onClick={(e) =>
                                handleNextOrPreviousDate(
                                    getSubsequentMonth(resolvedDate, true),
                                    e.target as HTMLButtonElement
                                )
                            }
                        >
                            {isMedium ? (
                                getSubsequentMonth(resolvedDate, true).toLocaleString({
                                    month: 'short',
                                    year: 'numeric',
                                })
                            ) : (
                                <FormattedMessage
                                    description="A word describing the next month"
                                    defaultMessage="Next"
                                />
                            )}
                            <ChevronRightIcon />
                        </ClubScheduleButton>
                    </BottomControls>
                </LoadingDisplay>
            )}
        </div>
    );
};

export default CalendarView;
